import React, { useEffect, useState } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { featureApis } from "../../../Data/axios";
import ReusableTable from "../ReusableTable";
import config from "../../../Data/AuthUser.js";
import DashboardFeaturesViewMore from "./DashboardFeaturesViewMore";
import DashboardFeaturesEdit from "./DashboardFeaturesEdit";


const DashboardFeatures = () => {
  const IMAGE_API_URL = config.apiImageUrl;
  const [features, setFeatures] = useState([]); // Initialize as an empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });

  const [newFeature, setNewFeature] = useState({
    title: "",
    image: null,
    description: "",
    Tutorial: "",
  });

  const headers = [
    { key: "id", label: "ID", sortable: true },
    { key: "createdby", label: "Author", sortable: true },
    { key: "title", label: "Title", sortable: true },
    { key: "description", label: "description", sortable: true },
    { key: "image", label: "Image", sortable: false },
    { key: "Tutorial", label: "Tutorial", sortable: true },
  ];

  const data = features.map((feature) => ({
    id: feature.id,
    title: feature.title,
    description: feature.description
      ? feature.description.substring(0, 25) +
        (feature.description.length > 25 ? "..." : "")
      : "",
    // image: feature.image,
    image: (
      <img
        src={`${IMAGE_API_URL}${feature.image}`}
        alt={feature.creator?.name || "404"}
        className="w-10 h-10 object-cover"
      />
    ),
    Tutorial: feature.Tutorial,
    // createdby: feature.createdby,
    createdby: (
      <div className="flex items-center gap-3">
        {feature && feature.user_image && (
          <img
            src={`${IMAGE_API_URL}${feature.user_image}`}
            alt={feature?.user_name || "Creator"}
            className="w-10 h-10 rounded-full object-cover"
          />
        )}
        <div>
          <div>{feature.user_name}</div>
          {feature && feature.user_name && (
            <div className="text-xs text-gray-500">{feature.user_role}</div>
          )}
        </div>
      </div>
    ),
    creator: {
      name: feature?.user_name || "Name",
      image: feature?.user_image || null,
      role: feature?.user_role || "Role",
      role_name: feature?.user_rolename || "Unknown",
    },
    rawData: feature // Store the complete feature object
  }));

  const fetchData = async () => {
    try {
      console.log("Fetching again");
      const response = await featureApis.getDashboardFeatures(queries);
      // const response = null;
      setFeatures(response?.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch features data");
      console.error("Error fetching features:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshData = async () => {
    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    refreshData();
  }, [queries]);

  const onPageChange = (newPage) => {
    setQueries((prev) => ({ ...prev, page: newPage }));
  };

  const onPerPageChange = (newPerPage) => {
    setQueries((prev) => ({ ...prev, per_page: newPerPage }));
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files?.length > 0) {
      setNewFeature((prev) => ({ ...prev, image: files[0] }));
    } else {
      setNewFeature((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddFeature = async () => {
    // Manually validate required fields
    if (
      !newFeature.title ||
      !newFeature.description ||
      !newFeature.Tutorial ||
      !newFeature.image
    ) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", newFeature.title);
      formData.append("description", newFeature.description);
      formData.append("image", newFeature.image);
      formData.append("Tutorial", newFeature.Tutorial);

      const response = await featureApis.add(formData);
      await fetchData();
      setShowModal(false);
      // Reset form data and errors
      setNewFeature({
        title: "",
        image: null,
        description: "",
        Tutorial: "",
      });
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to add new feature");
      console.log("Error adding feature:", err);
    } finally {
      setLoading(false);
    }
  };
  const handleTableAction = (action, rowData) => {
    console.log("Table action received:", action, rowData);
    
    if (action === 'view') {
      // Ensure we're getting the complete data
      const featureData = rowData.rawData || rowData;
      console.log("Opening view modal with data:", featureData);
      setSelectedFeature(featureData);
      setShowViewModal(true);
    } 
    else if (action === 'edit') {
      const featureData = rowData.rawData || rowData;
      console.log("Opening edit modal with data:", featureData);
      setSelectedFeature(featureData);
      setShowEditModal(true);
    } 
    else if (action === 'delete') {
      const featureId = rowData.id || (rowData.rawData && rowData.rawData.id);
      if (featureId) {
        handleDelete(featureId);
      } else {
        console.error("Could not find ID for deletion");
      }
    }
  };

  // Add this function to handle edit save
  const handleEditSave = async (updatedData) => {
    try {
      setLoading(true);
      await featureApis.updateById(selectedFeature.id, updatedData);
      setShowEditModal(false);
      await refreshData();
    } catch (error) {
      console.error("Failed to update feature:", error);
      setError("Failed to update feature: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // Add this function to handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this feature?")) {
      try {
        setLoading(true);
        await featureApis.deleteById(id);
        await refreshData();
      } catch (error) {
        console.error("Failed to delete feature:", error);
        setError("Failed to delete feature: " + (error.message || "Unknown error"));
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) return <div className="text-center py-10">Loading...</div>;
  // if (error)
  //   return <div className="text-center py-10 text-red-500">{error}</div>;

  return (
    <div className="container mx-auto bg-gray-200 p-4 min-h-[100vh] text-black">
      <div className="flex justify-between items-center mb-4 md:mx-6 mt-1">
        <h1 className="text-2xl font-bold font-ubuntu">Features</h1>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          onClick={() => setShowModal(true)}
        >
          Add Feature
        </button>
      </div>

      <div className="h-[50vh]">
        <ReusableTable
          headers={headers}
          data={data}
          rowKey="id"
          defaultSortKey="id"
          tableClassName=""
          headerClassName="text-gray-700"
          rowClassName="hover:bg-gray-200"
          tableApis={featureApis}
          onTableUpdate={refreshData}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          queries={queries}
          type="features"
          onAction={handleTableAction}
        />
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-[0vw] md:w-1/6"></div>
          <div className="bg-white p-6 rounded shadow-lg w-full md:w-[85vw] h-[88vh] flex flex-col">
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">
              Add New Feature
            </h2>
            <div className="border-t border-gray-200 mb-6"></div>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddFeature();
              }}
              className="flex flex-col space-y-8 flex-grow"
              noValidate={false}
            >
              {/* Flex layout for headers and inputs */}
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">Title</p>
                <input
                  type="text"
                  name="title"
                  placeholder="Title"
                  value={newFeature.title}
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  required
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">
                  Description
                </p>
                <textarea
                  name="description"
                  placeholder="Description"
                  value={newFeature.description}
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  rows="5"
                  required
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">Image</p>
                <input
                  type="file"
                  name="image"
                  accept="image/jpeg, image/png, image/jpg, image/gif"
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  required
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">
                  Tutorial
                </p>
                <input
                  type="text"
                  name="Tutorial"
                  placeholder="Tutorial"
                  value={newFeature.Tutorial}
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  required
                />
              </div>
              <div className="mt-6 flex justify-start space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* View Feature Modal */}
      {showViewModal && selectedFeature && (
        <DashboardFeaturesViewMore 
          data={selectedFeature.rawData || selectedFeature} 
          onClose={() => setShowViewModal(false)} 
        />
      )}

      {/* Edit Feature Modal */}
      {showEditModal && selectedFeature && (
        <DashboardFeaturesEdit 
          data={selectedFeature.rawData || selectedFeature}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default DashboardFeatures;
